import {useTranslation} from "react-i18next";
import AuthLayout from "../../layout/AuthLayout.jsx";
import AuthImage from "../../mosaic/images/auth-image.jpg";
import FormContainer from "../../components/form/FormContainer.jsx";
import FormControl from "../../components/form/FormControl.jsx";
import {useContext, useEffect, useState} from "react";
import FormCheckbox from "../../components/form/FormCheckbox.jsx";
import useDownloadState from "../../api/utilities/useDownloadState.js";
import FormButton from "../../components/form/FormButton.jsx";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import {AuthenticationApi, ExternalAuthenticationApi} from "../../api/authority/generated/index.ts";
import FormError from "../../components/form/FormError.jsx";
import {Link, useSearchParams} from "react-router-dom";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import toast from "react-hot-toast";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import FacebookLoginButton from "../../utils/brands/FacebookLoginButton.jsx";
import Or from "../../components/Or.jsx";
import GoogleLoginButton from "../../utils/brands/GoogleLoginButton.jsx";
import {isPreview, isDevelopment} from "../../utils/environment.js";
import {ls_read, ls_save} from "../../utils/localStorage.js";
import {useRedirectAuthenticatedEffect} from "../../components/auth/useRedirectAuthenticatedEffect.js";
import {getSafeMcpReturnPath} from './mcpConsent.js';

export function completeSignIn({newIdentity, setCurrentIdentity, rememberMe}) {
    if (rememberMe === undefined) {
        setCurrentIdentity(newIdentity);
        return;
    }
    setCurrentIdentity(newIdentity, rememberMe);
}

export async function runExternalSignIn({authenticate, setCurrentIdentity, setLoading, onError}) {
    setLoading(true);
    try {
        completeSignIn({newIdentity: await authenticate(), setCurrentIdentity});
    } catch (error) {
        await onError(error);
    } finally {
        setLoading(false);
    }
}

export default function SignIn() {
    const {t} = useTranslation();
    const {et} = useErrorTranslation();

    const [searchParams] = useSearchParams();
    const returnPath = getSafeMcpReturnPath(searchParams.get('returnTo')) ?? '/';
    useRedirectAuthenticatedEffect(returnPath);

    const [buttonClicked, setButtonClicked] = useState(false);
    const [signInState, setSignInLoading, setSignInResult, setSignInError] = useDownloadState();

    let credentialsInitialState = {
        'email': '',
        'password': '',
        'rememberMe': false,
    };


    if (isPreview() || isDevelopment()) {

        let randomKey = ls_read('demo_user');

        if (randomKey === null) {
            randomKey = (Math.random() + 1).toString(36).substring(7);
            ls_save('demo_user', randomKey);
        }

        credentialsInitialState = {
            'email': isPreview() ? (randomKey + '@datixo.com') : 'demo@datixo.com',
            'password': 'demo',
            'rememberMe': true,
        };
    }

    const [signInCredentials, setSignInCredentials] = useState(credentialsInitialState);

    const startSignInProcess = () => {
        setButtonClicked(true);
    };

    const {setCurrentIdentity, getAuthorityConfiguration} = useContext(GlobalContext);

    useEffect(() => {

        if (!buttonClicked || signInState.downloading) {
            return;
        }

        setButtonClicked(false);
        setSignInLoading(true);
        setSignInError(null);

        new AuthenticationApi(getAuthorityConfiguration())
            .signInRaw({signInCredentials: signInCredentials})
            .then((signInTokenResponseBody) => {
                setSignInLoading(false);
                if (signInTokenResponseBody.raw.status === 200) {
                    signInTokenResponseBody.raw.json().then((newIdentity) => {
                        completeSignIn({
                            newIdentity,
                            setCurrentIdentity,
                            rememberMe: signInCredentials.rememberMe,
                        });
                    });
                }
            })
            .catch((error) => setSignInError(error))
            .finally(() => setSignInLoading(false));
    }, [buttonClicked]);

    const facebookSuccess = (accessToken) => {
        void runExternalSignIn({
            authenticate: () => new ExternalAuthenticationApi(getAuthorityConfiguration()).facebookAccessTokenAuth({accessToken}),
            setCurrentIdentity,
            setLoading: setSignInLoading,
            onError: (e) => et(e).then((message) => { if (message) toast.error(message); }),
        });
    }
    const googleSuccess = (code) => {
        void runExternalSignIn({
            authenticate: () => new ExternalAuthenticationApi(getAuthorityConfiguration()).googleAccessTokenAuth({code}),
            setCurrentIdentity,
            setLoading: setSignInLoading,
            onError: (e) => et(e).then((message) => { if (message) toast.error(message); }),
        });
    };

    return (
        <AuthLayout title={t('auth.sign-in')} authImage={AuthImage}>
            <LoadingComponent loading={signInState.downloading}>
                <FormContainer>
                    {signInState.error && <FormError error={signInState.error}/>}

                    <FormControl
                        label={t('auth.email-address')}
                        type={'email'}
                        value={signInCredentials.email}
                        onChange={(e) => setSignInCredentials({...signInCredentials, email: e.target.value})}
                    />

                    <div className="pw-control-wrap">
                        <Link className="pw-control-link" to="/reset-password">
                            {t('auth.forgot-password-question')}
                        </Link>

                        <FormControl
                            label={t('auth.password')}
                            type={'password'}
                            value={signInCredentials.password}
                            onChange={(e) => setSignInCredentials({...signInCredentials, password: e.target.value})}
                        />
                    </div>

                    <FormCheckbox
                        label={t('auth.remember-me')}
                        checked={signInCredentials.rememberMe}
                        onChange={(e) => setSignInCredentials({...signInCredentials, rememberMe: e.target.checked})}
                    />

                    <div className="auth-btn-wrap">
                        <FormButton className={'button-primary'} onClick={() => startSignInProcess()}>
                            {t('auth.sign-in')}
                        </FormButton>
                    </div>

                    <div className="bottom-link-wrap mt-4 text-center">
                        {t('auth.no-account-question')}&nbsp;
                        <Link
                            className="font-medium text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400"
                            to="/sign/up"
                        >
                            {t('auth.call-to-register')}
                        </Link>
                    </div>
                </FormContainer>


                {!isPreview() &&
                    <>
                        <Or/>
                        <div className="comp__socialLogin">
                            {<FacebookLoginButton
                                configurationId={import.meta.env.VITE_META_LOGIN_CONFIGURATION_ID}
                                onSuccess={facebookSuccess}
                                className={'mb-4'}
                            >
                                {t('auth.continue-with-facebook')}
                            </FacebookLoginButton>}

                            <GoogleLoginButton
                                onSuccess={googleSuccess}
                                onError={(e) => et(e).then((message) => { if (message) toast.error(message); })}
                                className={'mb-3'}
                            >
                                {t('auth.continue-with-google')}
                            </GoogleLoginButton>
                        </div>
                    </>
                }
            </LoadingComponent>
        </AuthLayout>
    );
}
